import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowLeft,
  Check,
  Sparkles,
  Dot,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { WHATSAPP_URL } from "../lib/constants";
import { useT } from "../i18n";

const HERO_IMAGE =
  "https://customer-assets-0z36b82j.emergentagent.net/job_movimento-saude-1/artifacts/xryo7db3_pillll.jpeg";

const fade = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.75, ease: "easeOut" },
};

export default function PilatesPage() {
  const { t } = useT();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const howItems = t("pilatesPage.howItems");
  const equipItems = t("pilatesPage.equipItems");
  const accessoriesItems = t("pilatesPage.accessoriesItems");
  const indications = t("pilatesPage.indications");

  return (
    <div className="App bg-[#FEFEFE]" data-testid="pilates-page">
      <Navbar />

      {/* ═══════ HERO ═══════ */}
      <section
        data-testid="pilates-hero"
        className="relative min-h-[78vh] lg:min-h-[80vh] w-full overflow-hidden bg-[#0F1A17]"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Pilates Clínico na Yin Motion"
            className="w-full h-full object-cover object-[60%_40%] opacity-[0.55]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F1A17]/92 via-[#0F1A17]/65 to-[#0F1A17]/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A17]/55 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 min-h-[78vh] lg:min-h-[80vh] flex flex-col">
          <div className="flex-1 flex items-end pb-20 md:pb-28 pt-36 md:pt-44">
            <div className="w-full max-w-[1200px] mx-auto px-5 md:px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: "easeOut" }}
                className="max-w-[860px]"
              >
                <Link
                  to="/"
                  data-testid="pilates-back"
                  className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase font-semibold text-white/70 hover:text-[#EE8984] transition-colors mb-10"
                >
                  <ArrowLeft size={14} />
                  {t("pilatesPage.back")}
                </Link>

                <div className="flex items-center gap-3 mb-8">
                  <span className="h-px w-10 bg-[#EE8984]" />
                  <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#F7DEDA]">
                    {t("pilatesPage.eyebrow")}
                  </span>
                </div>

                <h1 className="font-serif text-[34px] sm:text-[44px] lg:text-[56px] xl:text-[64px] leading-[1.1] tracking-tight text-white font-light">
                  {t("pilatesPage.heroTitle")}
                </h1>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ INTRO ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#FEFEFE]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
          <motion.div {...fade} className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[#A3BD98]" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD]">
                {t("pilatesPage.introEyebrow")}
              </span>
            </div>
          </motion.div>
          <motion.div {...fade} className="lg:col-span-8">
            <p className="font-serif text-[22px] md:text-[28px] lg:text-[32px] leading-[1.4] tracking-tight text-[#2A322C] font-light mb-8">
              {t("pilatesPage.introHeadline")}
            </p>
            <p className="text-[16px] md:text-[17px] text-[#5C6660] leading-[1.85] max-w-[680px]">
              {t("pilatesPage.introBody")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══════ COMO TRABALHAMOS ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#EEF2E9]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div {...fade} className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[#A3BD98]" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD]">
                {t("pilatesPage.howEyebrow")}
              </span>
            </div>
            <h2 className="font-serif text-[32px] md:text-[40px] lg:text-[46px] leading-[1.15] tracking-tight text-[#2A322C] font-semibold">
              {t("pilatesPage.howTitle")}
            </h2>
            <p className="mt-6 text-[16px] md:text-[17px] text-[#5C6660] leading-[1.85] max-w-[480px]">
              {t("pilatesPage.howLead")}
            </p>
          </motion.div>

          <motion.ul {...fade} className="lg:col-span-7 space-y-5">
            {howItems.map((item, i) => (
              <li
                key={i}
                data-testid={`pilates-how-${i}`}
                className="group flex items-start gap-5 bg-white/70 backdrop-blur-sm border border-[#A3BD98]/15 rounded-[12px] px-7 py-6 hover:bg-white hover:border-[#A3BD98]/35 hover:-translate-y-[2px] transition-all duration-300 shadow-[0_6px_18px_-14px_rgba(42,50,44,0.12)]"
              >
                <span className="shrink-0 mt-[6px] w-5 h-5 rounded-full bg-[#A3BD98] flex items-center justify-center text-white">
                  <Check size={12} strokeWidth={3} />
                </span>
                <span className="text-[16px] md:text-[17px] text-[#2A322C] leading-[1.6] font-medium">
                  {item}
                </span>
              </li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* ═══════ EQUIPAMENTOS ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#FEFEFE]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6">
          <motion.div {...fade} className="max-w-3xl mb-14 md:mb-20">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[#EE8984]" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#CF8376]">
                {t("pilatesPage.equipEyebrow")}
              </span>
            </div>
            <h2 className="font-serif text-[32px] md:text-[40px] lg:text-[46px] leading-[1.15] tracking-tight text-[#2A322C] font-semibold">
              {t("pilatesPage.equipTitle")}
            </h2>
            <p className="mt-6 text-[16px] md:text-[17px] text-[#5C6660] leading-[1.85]">
              {t("pilatesPage.equipLead")}
            </p>
          </motion.div>

          <motion.div {...fade} className="mb-10 md:mb-14 rounded-[16px] overflow-hidden border border-[#A3BD98]/20 shadow-[0_18px_50px_-28px_rgba(42,50,44,0.28)]">
            <div className="relative aspect-[16/9] bg-[#EEF2E9]/40">
              <img
                src="https://customer-assets-0z36b82j.emergentagent.net/job_movimento-saude-1/artifacts/ayllt4gn_equipamentos%20.webp"
                alt="Estúdio Yin Motion com todos os grandes equipamentos de Pilates"
                loading="lazy"
                data-testid="equip-studio-photo"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </motion.div>

          <motion.ul
            {...fade}
            data-testid="equip-list"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4"
          >
            {equipItems.map((item, i) => (
              <li
                key={i}
                data-testid={`equip-item-${i}`}
                className="group flex items-center gap-5 bg-[#FEFEFE] border border-[#A3BD98]/20 rounded-full pl-5 pr-7 py-4 hover:border-[#A3BD98]/55 hover:translate-x-[3px] transition-all duration-300 shadow-[0_6px_18px_-14px_rgba(42,50,44,0.12)]"
              >
                <span className="shrink-0 w-9 h-9 rounded-full border border-[#A3BD98]/45 flex items-center justify-center text-[11px] tracking-[0.14em] font-semibold text-[#A3BD98] group-hover:bg-[#A3BD98] group-hover:text-white group-hover:border-[#A3BD98] transition-colors duration-300">
                  0{i + 1}
                </span>
                <span className="text-[16px] md:text-[18px] text-[#2A322C] leading-[1.3] font-semibold font-serif">
                  {item.name}
                </span>
              </li>
            ))}
          </motion.ul>

          {/* Accessories strip */}
          <motion.div {...fade} className="border-t border-[#E4E9DE] pt-10 mt-14">
            <p className="text-[11px] tracking-[0.28em] uppercase font-semibold text-[#5C6660] mb-5">
              {t("pilatesPage.accessoriesLabel")}
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-3">
              {accessoriesItems.map((a, i) => (
                <span
                  key={i}
                  data-testid={`accessory-${i}`}
                  className="inline-flex items-center gap-2 text-[14.5px] md:text-[15px] text-[#2A322C] bg-[#EEF2E9]/70 border border-[#A3BD98]/15 rounded-full px-5 py-2"
                >
                  <span className="w-[5px] h-[5px] rounded-full bg-[#A3BD98]" />
                  {a}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════ EXPERIÊNCIA (dark editorial) ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#0F1A17] overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(163,189,152,0.12),transparent_55%)] pointer-events-none"
        />
        <div className="relative max-w-[1200px] mx-auto px-5 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div {...fade} className="lg:col-span-5">
            <div
              data-testid="pilates-year-badge"
              className="font-serif text-[120px] md:text-[160px] lg:text-[200px] leading-none text-transparent font-light tracking-tight"
              style={{ WebkitTextStroke: "1.5px #A3BD98" }}
            >
              {t("pilatesPage.expYear")}
            </div>
            <div className="mt-4 text-[11px] tracking-[0.32em] uppercase font-semibold text-[#A3BD98]/80">
              {t("pilatesPage.expYearLabel")}
            </div>
          </motion.div>

          <motion.div {...fade} className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[#A3BD98]/70" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#A3BD98]/80">
                {t("pilatesPage.expEyebrow")}
              </span>
            </div>
            <h2 className="font-serif text-[32px] md:text-[40px] lg:text-[46px] leading-[1.15] tracking-tight text-white font-semibold">
              {t("pilatesPage.expTitle")}
            </h2>
            <p className="mt-7 text-[17px] md:text-[19px] text-white/75 leading-[1.85] font-light max-w-[620px]">
              {t("pilatesPage.expBody")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══════ INDICAÇÕES ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#FEFEFE]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6">
          <motion.div {...fade} className="max-w-3xl mb-12 md:mb-16">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[#A3BD98]" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD]">
                {t("pilatesPage.indicationsEyebrow")}
              </span>
            </div>
            <h2 className="font-serif text-[30px] md:text-[38px] lg:text-[44px] leading-[1.15] tracking-tight text-[#2A322C] font-semibold">
              {t("pilatesPage.indicationsTitle")}
            </h2>
          </motion.div>

          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 lg:gap-x-16">
            {indications.map((ind, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                data-testid={`indication-${i}`}
                className="group flex items-start gap-5 py-6 border-b border-[#E4E9DE] hover:border-[#A3BD98]/50 transition-colors"
              >
                <span className="font-serif text-[22px] md:text-[26px] font-light text-[#A3BD98] shrink-0 leading-none pt-1 group-hover:text-[#2A322C] transition-colors">
                  0{i + 1}
                </span>
                <span className="text-[16px] md:text-[17px] text-[#2A322C] leading-[1.6] font-medium">
                  {ind}
                </span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* ═══════ OBJETIVO ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#EEF2E9]">
        <motion.div {...fade} className="max-w-[900px] mx-auto px-5 md:px-6 text-center">
          <div className="inline-flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-[#A3BD98]" />
            <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD]">
              {t("pilatesPage.goalEyebrow")}
            </span>
            <span className="h-px w-10 bg-[#A3BD98]" />
          </div>
          <Sparkles size={28} strokeWidth={1.5} className="text-[#A3BD98] mx-auto mb-6" />
          <h2 className="font-serif text-[28px] md:text-[36px] lg:text-[42px] leading-[1.25] tracking-tight text-[#2A322C] font-semibold">
            {t("pilatesPage.goalTitle")}
          </h2>
          <p className="mt-7 text-[17px] md:text-[19px] text-[#5C6660] leading-[1.85] font-light max-w-2xl mx-auto">
            {t("pilatesPage.goalBody")}
          </p>
        </motion.div>
      </section>

      {/* ═══════ CTA FINAL ═══════ */}
      <section className="relative py-24 md:py-32 bg-[#FEFEFE] border-t border-[#E4E9DE]">
        <motion.div {...fade} className="max-w-[900px] mx-auto px-5 md:px-6 text-center">
          <p className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD] mb-6">
            {t("pilatesPage.ctaEyebrow")}
          </p>
          <h3 className="font-serif text-[26px] md:text-[34px] lg:text-[40px] leading-[1.25] tracking-tight text-[#2A322C] font-light max-w-2xl mx-auto">
            {t("pilatesPage.ctaTitle")}
          </h3>
          <div className="mt-10 flex items-center justify-center">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pilates-cta"
              className="group inline-flex items-center gap-2 bg-[#EE8984] text-white rounded-full px-9 py-[18px] text-[14px] font-semibold tracking-wide hover:bg-[#CF8376] hover:scale-[1.02] hover:shadow-[0_16px_40px_-12px_rgba(238,137,132,0.55)] transition-all duration-300"
            >
              {t("pilatesPage.ctaButton")}
              <ArrowUpRight
                size={17}
                className="group-hover:rotate-45 transition-transform duration-300"
              />
            </a>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
